import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { ThresholdMetricBox } from "@/components/ThresholdMetricBox"
import {
  getResponseLatencySeverity,
  type ResponseLatencySeverity,
} from "@/components/transcript/responseLatency"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"
import {
  resolveLatencyFilterRole,
  resolveLatencyGaps,
  type LatencyResultJson,
} from "./latencyGaps"

const DOCUMENTATION = `
This metric measures the latency of responses after filtering outliers, based on diarized audio analysis.
It calculates the response time between speaker turns in conversations.
`

const CATEGORY = "latency"

const REGION_COLOR_BY_SEVERITY: Record<ResponseLatencySeverity, string> = {
  normal: "rgba(74, 222, 128, 0.4)",
  slow: "rgba(251, 146, 60, 0.4)",
  "very-slow": "rgba(248, 113, 113, 0.4)",
}

function secondsToMs(seconds: number | null | undefined): number | null {
  return seconds != null && Number.isFinite(seconds)
    ? Math.floor(seconds * 1000)
    : null
}

function msToSeconds(milliseconds: number | null): number | null {
  return milliseconds == null ? null : milliseconds / 1000
}

interface LatencyStatsDisplay {
  medianLatency: number | null
  p90Latency: number | null
  minLatency: number | null
  maxLatency: number | null
  stdDev: number | null
  numOutliers: number
  meanLatency: number | null
}

const STAT_NAME = "latency_seconds"

function buildStatsFromTyped(
  mo: RunMetricOutput | MetricOutputDisplaySource,
): LatencyStatsDisplay | null {
  const summary = getStat<{
    min?: number
    max?: number
    mean?: number
    median?: number
  }>(mo, "summary", STAT_NAME)
  const dispersion = getStat<{ std_dev?: number }>(mo, "dispersion", STAT_NAME)
  const percentiles = getStat<{ p90?: number }>(mo, "percentiles", STAT_NAME)
  const outliers = getStat<{ count?: number }>(mo, "outliers", STAT_NAME)

  if (!summary && !dispersion && !percentiles && !outliers) {
    return null
  }

  return {
    medianLatency: secondsToMs(summary?.median),
    p90Latency: secondsToMs(percentiles?.p90),
    minLatency: secondsToMs(summary?.min),
    maxLatency: secondsToMs(summary?.max),
    stdDev: secondsToMs(dispersion?.std_dev),
    numOutliers: outliers?.count ?? 0,
    meanLatency: secondsToMs(summary?.mean),
  }
}

function buildStatsFromLegacy(
  data: LatencyResultJson | null | undefined,
): LatencyStatsDisplay | null {
  const statistics = data?.latency_analysis?.statistics
  if (!statistics) {
    return null
  }
  const percentiles = statistics.distribution_metrics?.percentiles ?? {}
  const outlierMetadata = statistics.outlier_analysis?.metadata ?? {}
  return {
    medianLatency: secondsToMs(statistics.median_latency),
    p90Latency: secondsToMs(percentiles.p90),
    minLatency: secondsToMs(statistics.min_latency),
    maxLatency: secondsToMs(statistics.max_latency),
    stdDev: secondsToMs(statistics.std_dev),
    numOutliers: outlierMetadata.outlier_count || 0,
    meanLatency: secondsToMs(
      statistics.average_latency ?? statistics.mean_latency,
    ),
  }
}

function resolveLatencyStats(
  mo: RunMetricOutput | MetricOutputDisplaySource,
): LatencyStatsDisplay | null {
  return (
    buildStatsFromTyped(mo) ??
    buildStatsFromLegacy(parseResultJson<LatencyResultJson>(mo))
  )
}

/** Format latency for display: always "X.XX s". */
function formatLatencySeconds(seconds: number): string {
  return `${seconds.toFixed(2)} s`
}

export class LatencyMetricRecord extends NumericalMetricRecord {
  documentation = DOCUMENTATION
  overviewSize = "medium" as const
  unit = "s"
  aggregate_units = "s"

  protected _hasValidDeepDiveData(mo: RunMetricOutput): boolean {
    if (super._hasValidDeepDiveData(mo)) {
      return true
    }
    const statistics = resolveLatencyStats(mo)
    return (
      statistics != null &&
      [
        statistics.meanLatency,
        statistics.stdDev,
        statistics.minLatency,
        statistics.p90Latency,
      ].some(Number.isFinite)
    )
  }

  protected _formatValue(v: number): string {
    return v.toFixed(2)
  }

  // ─── Audio regions ────────────────────────────────────────────────────────

  getAudioRegions(
    mo: MetricOutputDisplaySource,
    _options?: { theme?: "light" | "dark" },
  ): AudioRegionParams[] {
    try {
      return resolveLatencyGaps(mo).map((gap, index) => ({
        id: `latency:${index}`,
        start: gap.start,
        end: gap.end,
        color:
          REGION_COLOR_BY_SEVERITY[
            getResponseLatencySeverity(gap.latencySeconds)
          ],
        drag: false,
        resize: false,
        content: formatLatencySeconds(gap.latencySeconds),
      }))
    } catch {
      return []
    }
  }

  // ─── Human review ──────────────────────────────────────────────────────────

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "mean_seconds",
      useRegions: true,
      buildReviewedResultJson: ({ editedCount, score, metricOutput }) => ({
        mean_latency: Number(score.toFixed(3)),
        total_measurements: editedCount,
        filter_role: resolveLatencyFilterRole(metricOutput) ?? null,
      }),
    }
  }

  // ─── Deep dive ────────────────────────────────────────────────────────────

  renderDeepDivePanel(
    notesNode: React.ReactNode,
    metricOutput: RunMetricOutput | undefined,
  ): React.ReactNode {
    if (!metricOutput) {
      return null
    }
    const statistics = resolveLatencyStats(metricOutput)
    if (!statistics) {
      return null
    }

    return (
      <div className="grid grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4">
          <ThresholdMetricBox
            title="Mean Latency"
            value={msToSeconds(statistics.meanLatency)}
            ranges={{ danger: 4.5, warning: 2.5 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="Standard Deviation"
            value={msToSeconds(statistics.stdDev)}
            ranges={{ danger: 1.5, warning: 0.5 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="Min Latency"
            value={msToSeconds(statistics.minLatency)}
            ranges={{ danger: 3, warning: 2 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="P90 Latency"
            value={msToSeconds(statistics.p90Latency)}
            ranges={{ danger: 6, warning: 3 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
        </div>
        <div>{notesNode}</div>
      </div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const statistics = resolveLatencyStats(mo)
    if (!statistics) {
      return <div>No detailed latency data available</div>
    }

    return (
      <div key={this.metric_name} className="w-full">
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-4">
            <ThresholdMetricBox
              title="Mean Latency"
              value={msToSeconds(statistics.meanLatency)}
              ranges={{ danger: 4.5, warning: 2.5 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="Standard Deviation"
              value={msToSeconds(statistics.stdDev)}
              ranges={{ danger: 1.5, warning: 0.5 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="Min Latency"
              value={msToSeconds(statistics.minLatency)}
              ranges={{ danger: 3, warning: 2 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="P90 Latency"
              value={msToSeconds(statistics.p90Latency)}
              ranges={{ danger: 6, warning: 3 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
          </div>
        </div>
      </div>
    )
  }
}
